import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { apiRequest } from "../lib/api";
import type { SessionResponse, SessionRole } from "../types";

interface SessionContextValue {
  authenticated: boolean;
  checking: boolean;
  role: SessionRole | null;
  csrfToken: string | null;
  login: (password: string, requestedRole: SessionRole) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<SessionRole | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  const clearSession = useCallback(() => {
    setRole(null);
    setCsrfToken(null);
  }, []);

  useEffect(() => {
    let active = true;
    apiRequest<SessionResponse & { authenticated: boolean }>("/api/v1/session")
      .then((session) => {
        if (!active || !session.authenticated) return;
        setRole(session.role);
        setCsrfToken(session.csrfToken ?? null);
      })
      .catch(() => {
        if (active) clearSession();
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    const onUnauthorized = () => clearSession();
    window.addEventListener("faro:unauthorized", onUnauthorized);
    return () => {
      active = false;
      window.removeEventListener("faro:unauthorized", onUnauthorized);
    };
  }, [clearSession]);

  const login = useCallback(async (password: string, requestedRole: SessionRole) => {
    const session = await apiRequest<SessionResponse>("/api/v1/session", {
      method: "POST",
      body: { password, role: requestedRole },
    });
    setRole(session.role ?? requestedRole);
    setCsrfToken(session.csrfToken ?? null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest<void>("/api/v1/session", { method: "DELETE", csrfToken });
    } finally {
      clearSession();
    }
  }, [clearSession, csrfToken]);

  const value = useMemo<SessionContextValue>(() => ({
    authenticated: role !== null,
    checking,
    role,
    csrfToken,
    login,
    logout,
  }), [checking, csrfToken, login, logout, role]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession debe utilizarse dentro de SessionProvider");
  return value;
}
